import { MessageCircle, Phone, StickyNote, Tag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/ui/sheet.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs.jsx";
import { initials, formatDateTime } from "@/shared/lib/format.js";
import { LeadChatTab } from "./LeadChatTab.jsx";
import { LeadNotesTab } from "./LeadNotesTab.jsx";
import { stageById, temperatureDot, temperatureLabel } from "../lead-constants.js";

/**
 * Painel lateral do lead.
 *
 * ESTE componente é o motivo de a pasta `features/leads` existir: no app
 * antigo ele estava copiado, idêntico e com ~230 linhas, dentro de
 * `routes/leads.tsx` e `routes/crm.tsx`. Qualquer ajuste precisava ser
 * feito duas vezes, e as duas cópias já estavam começando a divergir.
 *
 * @param {object} props
 * @param {import("../use-leads.js").Lead | null} props.lead
 * @param {() => void} props.onClose
 */
export function LeadDrawer({ lead, onClose }) {
  if (!lead) return null;

  const etapa = stageById(lead.status);

  return (
    <Sheet open={Boolean(lead)} onOpenChange={(aberto) => !aberto && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="border-b border-border/60 p-5">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-chart-2/20 text-base font-semibold">
              {initials(lead.name)}
            </div>

            <div className="min-w-0 flex-1 text-left">
              <SheetTitle className="truncate">{lead.name}</SheetTitle>

              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                {lead.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {lead.phone}
                  </span>
                )}
                {lead.procedure && <span>{lead.procedure}</span>}
                {lead.origin && <span>{lead.origin}</span>}
              </div>

              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge variant="secondary" className="text-[10px]">
                  <span className={`mr-1 h-1.5 w-1.5 rounded-full ${temperatureDot(lead.temperature)}`} />
                  {temperatureLabel(lead.temperature)}
                </Badge>
                {lead.campaign && (
                  <Badge variant="outline" className="text-[10px]">
                    <Tag className="mr-1 h-2.5 w-2.5" />
                    {lead.campaign}
                  </Badge>
                )}
                <Badge variant="secondary" className={`text-[10px] ${etapa.tone}`}>
                  {etapa.title}
                </Badge>
              </div>
            </div>
          </div>
        </SheetHeader>

        <Tabs defaultValue="whatsapp" className="flex flex-1 flex-col overflow-hidden">
          <TabsList className="mx-5 mt-3 grid w-auto grid-cols-3">
            <TabsTrigger value="whatsapp" className="gap-1.5">
              <MessageCircle className="h-3.5 w-3.5" />
              WhatsApp
            </TabsTrigger>
            <TabsTrigger value="notas" className="gap-1.5">
              <StickyNote className="h-3.5 w-3.5" />
              Histórico
            </TabsTrigger>
            <TabsTrigger value="info" className="gap-1.5">
              Informações
            </TabsTrigger>
          </TabsList>

          {/* `forceMount` não é usado de propósito: cada aba só busca os
              próprios dados quando é aberta. */}
          <TabsContent value="whatsapp" className="mt-0 flex flex-1 flex-col overflow-hidden">
            <LeadChatTab leadId={lead.id} />
          </TabsContent>

          <TabsContent value="notas" className="mt-0 flex flex-1 flex-col overflow-hidden">
            <LeadNotesTab leadId={lead.id} />
          </TabsContent>

          <TabsContent value="info" className="scrollbar-slim mt-0 flex-1 overflow-y-auto p-5">
            <LeadInfo lead={lead} />
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function LeadInfo({ lead }) {
  const campos = [
    ["Procedimento", lead.procedure],
    ["Telefone", lead.phone],
    ["Origem", lead.origin],
    ["Campanha", lead.campaign],
    ["Canal", lead.channel],
    ["Responsável", lead.owner],
    ["Etapa", stageById(lead.status).title],
    ["Entrada", formatDateTime(lead.createdAt)],
    ["Última interação", formatDateTime(lead.lastInteractionAt)],
  ];

  return (
    <>
      <dl className="grid grid-cols-2 gap-4 text-sm">
        {campos.map(([rotulo, valor]) => (
          <div key={rotulo}>
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {rotulo}
            </dt>
            <dd className="mt-0.5 text-sm">{valor || "—"}</dd>
          </div>
        ))}
      </dl>

      {lead.summary && (
        <div className="mt-5">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Resumo
          </p>
          <p className="text-sm">{lead.summary}</p>
        </div>
      )}
    </>
  );
}
